import { useLayoutEffect, useRef, useSyncExternalStore, type RefObject } from 'react';
import { stairwaysFrom, type Stairway } from '../core/geometry/stairwells';
import { modelBoundsVersion, subscribeModelBounds } from '../core/geometry/modelBounds';
import { t } from '../i18n/I18n';
import { Icon } from './Icon';
import { useStore } from './useStore';
import type { SceneHandle } from './Scene3D';

const subscribeBounds = (listener: () => void) => subscribeModelBounds(() => listener());

/**
 * «Спуститься по лестнице» / «Подняться по лестнице» on the row of «?»: one for each way the floor's stairs lead
 * (`stairwaysFrom`), worked out again when a flight's model is measured, since that moves its stairwell.
 */
export function StairsButtons({ scene, besideHelp }: { scene: RefObject<SceneHandle | null>; besideHelp: boolean }) {
  const { levels, index } = useStore(s => ({ levels: s.scene?.levels, index: s.activeLevelIndex }));
  useSyncExternalStore(subscribeBounds, modelBoundsVersion);
  const directions = levels ? [...new Set(stairwaysFrom(levels, index).map(way => way.direction))] : [];
  const refocus = useRef<Stairway['direction'] | null>(null);
  const row = useRef<HTMLDivElement | null>(null);
  // The button pressed with the keyboard may be gone on the new floor: the focus stays on the row, or goes to «?».
  useLayoutEffect(() => {
    const wanted = refocus.current;
    if (!wanted) return;
    refocus.current = null;
    const buttons = [...(row.current?.querySelectorAll<HTMLButtonElement>('.stairs-button') ?? [])];
    (buttons.find(button => button.dataset.direction === wanted) ?? buttons[0] ?? document.querySelector<HTMLElement>('.help-menu .dropdown-trigger'))?.focus();
  });
  if (!directions.length) return null;
  return <div ref={row} className={'stairs-buttons' + (besideHelp ? ' beside-help' : '')}>
    {directions.map(direction => {
      const label = direction === 'up' ? t('#WebGoUpStairs') : t('#WebGoDownStairs');
      return <button key={direction} type="button" className="round stairs-button" data-direction={direction} aria-label={label} data-tip={label} data-tip-side="above"
        onClick={event => { const focused = event.currentTarget === document.activeElement; if (scene.current?.goStairs(direction) && focused) refocus.current = direction; }}>
        <Icon name={direction === 'up' ? 'stairsUp' : 'stairsDown'} size={22} /></button>;
    })}
  </div>;
}
